<script setup lang="ts">
import { getPageLink } from '~/utils/graphql/graphql'
import {
  GetFeaturedItemsDocument,
  GetSavedAndInProgressDocument,
} from '~/utils/generated/graphql'
import { useGraphQuery } from '~/utils/graphql/client'
import PPicture from '~/components/PPicture.vue'

const { locale } = useI18n()
const { data: featured } = await useGraphQuery(GetFeaturedItemsDocument, { locale: locale })
const { data: profileItems, refresh } = await useGraphQuery(GetSavedAndInProgressDocument, null, true)

const journey = computed(() => {
  const profile = profileItems.value?.profile
  if (!profile) return []
  const studies = profile.inprogress.map((s) => {
    return {
      id: s.id,
      slug: s.study!.slug,
      title: s.study!.title,
      media: s.study!.media!,
      nextSessionId: s.nextSessionId,
      profile: s,
      authors: s.study!.authors,
      __typename: 'Study',
    }
  })
  return studies.concat(profile.saved)
})

defineProps<{
  toggleOpen: () => void
}>()

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let refreshTimeout: any | null = null

function refreshSavedAndInProgress() {
  if (refreshTimeout) clearTimeout(refreshTimeout)
  refreshTimeout = setTimeout(() => {
    refreshTimeout = null
    refresh()
  }, 1_000)
}

const user = useFirebaseUser()
watch(user, async (currentUser, previousUser) => {
  if (currentUser && !previousUser) {
    refreshSavedAndInProgress()
  }
})

onMounted(() => {
  window.addEventListener('refresh-header', refreshSavedAndInProgress)
})

onUnmounted(() => {
  window.removeEventListener('refresh-header', refreshSavedAndInProgress)
})

function toDateString(item) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const date = item.date
  if (!date) return ''
  const d = new Date(Date.parse(date))
  return d.toLocaleString('default', { month: 'short' }) + ' ' + d.getDate()
}
</script>

<template>
  <div>
    <div class="meta-info mb-2">
      {{ journey.length > 0 ? $t('resumeJourney') : $t('featuredResources') }}
    </div>
    <div class="flex w-full overflow-x-auto gap-4 pb-4">
      <PLink
        v-for="(item, index) in (journey.length > 0 ? journey : (featured?.collection?.items?.items || []))"
        :id="'journey-item-link-' + index"
        :key="item.id"
        type="card"
        :to="getPageLink(item)"
        :content="[{ id: item.id, type: item.__typename || '', index: index, percent: undefined }]"
        :extras="{ slug: item.slug }"
        :click="toggleOpen"
        class="flex-none w-[241px] border-[1.5px] p-4 border-gray-200 rounded-lg overflow-hidden hover:bg-white hover:drop-shadow-md"
      >
        <div class="flex flex-col w-full h-full">
          <ContentImage
            :item="item"
            class="flex-none w-[209px] h-[209px] object-cover rounded-lg overflow-hidden"
          />
          <BadgesBadge
            class="mt-2 no-underline"
            :item="item"
            no-underline
          />
          <div class="mt-1 p line-clamp-2 grow">
            {{ item.title }}
          </div>
          <div
            v-if="item.authors && item.authors.length"
            class="flex flex-row mt-3 mb-1 text-xs place-items-center text-neutral-700"
          >
            <PPicture
              class="w-5 h-5 rounded-full object-cover shrink me-2"
              :item="item.authors[0]"
            />
            <div class="font-sans">
              {{ item.authors[0].title }}
            </div>
            <span
              v-if="item.__typename === 'Message'"
              class="text-neutral-700"
            >
              <span class="px-1">|</span>
              {{ toDateString(item) }}
            </span>
            <span
              v-else-if="typeof item.duration !== 'undefined'"
              class="text-neutral-700"
            ><span class="px-1">|</span>{{ $t('nMins', { n: item.duration }) }}</span>
            <span
              v-else-if="typeof item.sessionsTotal !== 'undefined'"
              class="text-neutral-700"
            ><span class="px-1">|</span>{{ $t('nDays', { n: item.sessionsTotal }) }}</span>
          </div>
        </div>
      </PLink>
    </div>
  </div>
</template>
